import type { RulesColor, RulesPiece } from '@/game/rules';
import type { GameSessionState } from '@/game/session';

export type BattleCueKind = 'move' | 'capture' | 'castle' | 'promotion' | 'check' | 'terminal';

export interface BattleCastleCue {
  readonly side: 'kingside' | 'queenside';
  readonly rookFrom: string;
  readonly rookTo: string;
}

/**
 * A short-lived presentation value for exactly one accepted live append.
 * It is intentionally value-only and is never written back to game state.
 */
export interface BattleCue {
  readonly id: string;
  readonly gameId: string;
  readonly gameEpoch: number;
  readonly revision: number;
  readonly from: string;
  readonly to: string;
  readonly moverColor: RulesColor;
  readonly moverPiece: RulesPiece;
  readonly capturedPiece?: RulesPiece;
  readonly captureSquare?: string;
  readonly castle?: BattleCastleCue;
  readonly promotion?: 'q' | 'r' | 'b' | 'n';
  readonly check: boolean;
  readonly terminal: boolean;
  readonly kind: BattleCueKind;
}

function castleFor(
  from: string,
  to: string,
  kingside: boolean,
): BattleCastleCue {
  const rank = from[1] ?? to[1] ?? '1';
  return Object.freeze(kingside
    ? { side: 'kingside' as const, rookFrom: `h${rank}`, rookTo: `f${rank}` }
    : { side: 'queenside' as const, rookFrom: `a${rank}`, rookTo: `d${rank}` });
}

function kindFor({
  terminal,
  check,
  promotion,
  castle,
  capture,
}: {
  readonly terminal: boolean;
  readonly check: boolean;
  readonly promotion: boolean;
  readonly castle: boolean;
  readonly capture: boolean;
}): BattleCueKind {
  if (terminal) return 'terminal';
  if (check) return 'check';
  if (promotion) return 'promotion';
  if (castle) return 'castle';
  if (capture) return 'capture';
  return 'move';
}

/**
 * Projects a cue only when `current` is exactly one accepted append from the
 * previous live presentation state. The legal-move descriptor from `previous`
 * is the sole source of move/capture/castle/promotion metadata.
 */
export function projectBattleCue(
  previous: GameSessionState | undefined,
  current: GameSessionState,
): BattleCue | undefined {
  if (
    previous === undefined
    || previous.gameId !== current.gameId
    || previous.gameEpoch !== current.gameEpoch
    || previous.review !== undefined
    || current.review !== undefined
    || previous.result.terminal
    || current.acceptedMoves.length !== previous.acceptedMoves.length + 1
    || current.position.revision !== previous.position.revision + 1
  ) {
    return undefined;
  }

  const accepted = current.acceptedMoves.at(-1);
  if (accepted === undefined) {
    return undefined;
  }

  const legalMove = previous.position.legalMoves.find((move) => move.uci === accepted.uci);
  if (legalMove === undefined) {
    return undefined;
  }

  const terminal = current.result.terminal;
  const check = current.position.checkedColor !== undefined;
  const castle = legalMove.isCastle
    ? castleFor(legalMove.from, legalMove.to, legalMove.isKingsideCastle)
    : undefined;
  const isCapture = legalMove.isCapture || legalMove.isEnPassant || legalMove.captured !== undefined;
  const captureSquare = isCapture
    ? legalMove.isEnPassant
      ? `${legalMove.to[0] ?? ''}${legalMove.from[1] ?? ''}`
      : legalMove.to
    : undefined;
  const kind = kindFor({
    terminal,
    check,
    promotion: legalMove.isPromotion,
    castle: legalMove.isCastle,
    capture: isCapture,
  });

  return Object.freeze({
    id: `${current.gameId}:${current.gameEpoch}:${current.position.revision}:${accepted.uci}`,
    gameId: current.gameId,
    gameEpoch: current.gameEpoch,
    revision: current.position.revision,
    from: legalMove.from,
    to: legalMove.to,
    moverColor: legalMove.color,
    moverPiece: legalMove.piece,
    ...(legalMove.captured === undefined ? {} : { capturedPiece: legalMove.captured }),
    ...(captureSquare === undefined ? {} : { captureSquare }),
    ...(castle === undefined ? {} : { castle }),
    ...(legalMove.promotion === undefined ? {} : { promotion: legalMove.promotion }),
    check,
    terminal,
    kind,
  });
}
